import { uniq } from "lodash-es"
import { Employment, PostTerminationPaymentFrequency } from "@oysterhr/schema"
import { RestrictiveCovenantsFormValues, AreaOfBusinessKnowledgeBase } from "../types"
import { RestrictiveCovenantsRulesFragment } from "~/gql/graphql"

type ParseParams = {
  knowledgeBase?: RestrictiveCovenantsRulesFragment
  areaOfBusinessKnowledgeBase?: AreaOfBusinessKnowledgeBase
  currentValues?: RestrictiveCovenantsFormValues
}

export const parseAmountType = ({ knowledgeBase, currentValues }: ParseParams) => {
  const allPaymentRules = knowledgeBase?.postTermination?.paymentRules
  const validAmountTypes = allPaymentRules?.map(rule => rule.amountType)

  const isDisabledField =
    (validAmountTypes &&
      validAmountTypes.length === 1 &&
      currentValues?.postTerminationSection?.amountType === validAmountTypes[0]) ??
    false

  const requiredAmountType =
    validAmountTypes && validAmountTypes.length === 1 ? validAmountTypes[0] : undefined

  return {
    validAmountTypes,
    isDisabledField,
    requiredAmountType,
  }
}

export const parsePaymentFrequency = ({ knowledgeBase, currentValues }: ParseParams) => {
  const selectedAmountType = currentValues?.postTerminationSection?.amountType
  let validPaymentFrequencies: PostTerminationPaymentFrequency[] = []
  if (selectedAmountType) {
    const allPaymentRules = knowledgeBase?.postTermination?.paymentRules
    const relevantPaymentRule = allPaymentRules?.find(
      rule => rule.amountType === selectedAmountType,
    )
    validPaymentFrequencies =
      relevantPaymentRule?.frequencyRules?.map(item => item?.frequency) ?? []
  }

  const isDisabledField =
    (validPaymentFrequencies &&
      validPaymentFrequencies.length === 1 &&
      currentValues?.postTerminationSection?.paymentFrequency === validPaymentFrequencies[0]) ??
    false

  const requiredPaymentFrequency =
    validPaymentFrequencies && validPaymentFrequencies.length === 1
      ? validPaymentFrequencies[0]
      : undefined

  return {
    validPaymentFrequencies,
    isDisabledField,
    requiredPaymentFrequency,
  }
}

// @todo: properly type once the api is done
const getValidRestrictedAreasRules = ({ knowledgeBase, currentValues }: ParseParams) => {
  const allPaymentRules = knowledgeBase?.postTermination?.paymentRules
  const allFrequencyRules = allPaymentRules?.flatMap(rule => rule.frequencyRules)
  const allRestrictedAreaRules = allFrequencyRules?.map(rule => rule?.restrictedAreaRules)
  // If any of the restrictedAreas rules are an empty array,
  // that means that the restrictedAreas is not applicable for that country
  // https://linear.app/oyster/issue/HQS-7/[company][staff]-kb-hiring-flow-step-4-hide-and-show-restrictive-areas
  const isNotApplicable = allRestrictedAreaRules?.some(rule => !rule?.length)
  if (isNotApplicable) {
    return {
      isApplicable: false,
      validRestrictedAreas: [],
      rules: [],
      additionalAreasAllowed: false,
    }
  }

  const selectedAmountType = currentValues?.postTerminationSection?.amountType
  const selectedPaymentFrequency = currentValues?.postTerminationSection?.paymentFrequency
  const additionalAreasAllowed = allRestrictedAreaRules
    ?.flat()
    .some(rule => rule.additionalAreasAllowed)

  if (selectedAmountType && selectedPaymentFrequency) {
    // If I have a selected amountType and paymentFrequency
    // I can filter the restricted areas to the relevant rule
    const relevantPaymentRule = allPaymentRules?.find(r => r.amountType === selectedAmountType)
    const relevantFrequencyRule = relevantPaymentRule?.frequencyRules?.find(
      r => r?.frequency === selectedPaymentFrequency,
    )
    const rules = relevantFrequencyRule?.restrictedAreaRules ?? []
    const validRestrictedAreas = rules?.map(rule => rule?.restrictedArea) ?? []

    return {
      isApplicable: true,
      validRestrictedAreas,
      rules,
      additionalAreasAllowed,
    }
  } else {
    // Otherwise, I can just combine all the restricted areas rules
    // This is to be used as an initial value for allowed the restricted areas
    // while the amountType and paymentFrequency are not selected
    const allCombinedRestrictedAreas = uniq(
      allRestrictedAreaRules?.flat().map(rule => rule?.restrictedArea),
    )
    return {
      isApplicable: true,
      validRestrictedAreas: allCombinedRestrictedAreas,
      rules: [],
      additionalAreasAllowed,
    }
  }
}

export const parseRestrictedAreas = ({ knowledgeBase, currentValues }: ParseParams) => {
  const { validRestrictedAreas, rules, isApplicable, additionalAreasAllowed } =
    getValidRestrictedAreasRules({
      knowledgeBase,
      currentValues,
    })

  return {
    isApplicable,
    validRestrictedAreas,
    rules,
    additionalAreasAllowed,
  }
}

export const getSingleRelevantPaymentRule = ({ knowledgeBase, currentValues }: ParseParams) => {
  const selectedAmountType = currentValues?.postTerminationSection?.amountType
  const selectedPaymentFrequency = currentValues?.postTerminationSection?.paymentFrequency
  const selectedRestrictedArea = currentValues?.postTerminationSection?.restrictedArea
  const selectedAdditionalAreas = currentValues?.postTerminationSection?.additionalAreas || []

  if (selectedAmountType && selectedPaymentFrequency) {
    const allPaymentRules = knowledgeBase?.postTermination?.paymentRules
    const relevantPaymentRule = allPaymentRules?.find(r => r.amountType === selectedAmountType)
    const relevantFrequencyRule = relevantPaymentRule?.frequencyRules.find(
      r => r.frequency === selectedPaymentFrequency,
    )
    const relevantRestrictedAreaRule = relevantFrequencyRule?.restrictedAreaRules.find(
      r =>
        r.restrictedArea === selectedRestrictedArea &&
        r.additionalAreasAllowed === selectedAdditionalAreas.length > 0,
    )
    if (!relevantRestrictedAreaRule) {
      return relevantFrequencyRule
    }
    return relevantRestrictedAreaRule
  } else {
    return null
  }
}

type IsAreaOfBusinessRequiredArgs = Pick<ParseParams, "areaOfBusinessKnowledgeBase"> &
  Pick<Employment, "subdivisionCode">
export const isAreaOfBusinessRequired = ({
  areaOfBusinessKnowledgeBase,
  subdivisionCode,
}: IsAreaOfBusinessRequiredArgs) => {
  if (!areaOfBusinessKnowledgeBase) return false

  const { companyAreaOfBusinessFieldRequired, companyAreaOfBusinessFieldForSubdivisionCodes } =
    areaOfBusinessKnowledgeBase
  if (companyAreaOfBusinessFieldRequired) {
    if (subdivisionCode && companyAreaOfBusinessFieldForSubdivisionCodes?.length) {
      return companyAreaOfBusinessFieldForSubdivisionCodes?.includes(subdivisionCode)
    }
    return true
  }
  return false
}
